import { useEffect, useRef, useState } from "react";
import { Check, Copy, FileText, Film, Trash2, UploadCloud } from "lucide-react";
import { api, mediaUrl, uploadMedia } from "@/lib/api";

const fmtSize = n => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const AdminMedia = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(null);
  const [copied, setCopied] = useState("");
  const [drag, setDrag] = useState(false);
  const ref = useRef();

  const load = () => api(`/admin/media?search=${encodeURIComponent(search)}`).then(d => setItems(d.items || [])).catch(e => setStatus(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  const uploadFiles = async files => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setStatus("");
    for (let i = 0; i < list.length; i++) {
      setProgress({ done: i, total: list.length, name: list[i].name });
      try { await uploadMedia(list[i]); }
      catch (e) { setStatus(`${list[i].name}: ${e.message}`); }
    }
    setProgress(null);
    load();
  };

  const remove = async m => {
    if (!window.confirm(`Remove "${m.name}" from the media library?`)) return;
    try { await api(`/admin/media/${m.id}`, { method: "DELETE" }); load(); } catch (e) { setStatus(e.message); }
  };
  const copy = m => { navigator.clipboard?.writeText(mediaUrl(m.url)); setCopied(m.id); setTimeout(() => setCopied(""), 1500); };

  return (
    <div className="media-panel" data-testid="admin-media-panel">
      <div
        className={drag ? "dropzone active" : "dropzone"}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); }}
        onClick={() => ref.current?.click()}
        data-testid="media-dropzone"
      >
        <UploadCloud size={30} />
        <b>{progress ? `Uploading ${progress.done + 1} of ${progress.total} — ${progress.name}` : "Upload photos, videos or documents from your device"}</b>
        <span>Drag & drop or click to browse · JPG, PNG, WEBP, PDF, MP4 · up to 10 MB each</span>
        {progress && <div className="progress"><i style={{ width: `${((progress.done) / progress.total) * 100}%` }} /></div>}
        <input ref={ref} type="file" multiple hidden accept="image/*,application/pdf,video/mp4" onChange={e => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="media-file-input" />
      </div>
      {status && <div className="error-message" data-testid="media-status">{status}</div>}
      <div className="admin-toolbar">
        <div className="search-box"><input placeholder="Search files…" value={search} onChange={e => setSearch(e.target.value)} data-testid="media-search" /></div>
        <span className="pill" data-testid="media-count">{items.length} files</span>
      </div>
      <div className="media-grid">
        {items.map(m => (
          <article className="media-card" key={m.id} data-testid={`media-card-${m.id}`}>
            <div className="media-thumb">
              {m.type?.startsWith("image/") ? <img src={mediaUrl(m.url)} alt={m.name} loading="lazy" /> : m.type?.startsWith("video/") ? <Film size={28} /> : <FileText size={28} />}
            </div>
            <div className="media-meta">
              <b title={m.name}>{m.name}</b>
              <small>{m.type?.split("/")[1]?.toUpperCase()} · {fmtSize(m.size || 0)}</small>
            </div>
            <div className="media-actions">
              <button type="button" className="link" onClick={() => copy(m)} data-testid={`media-copy-${m.id}`}>{copied === m.id ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy URL</>}</button>
              <a className="link" href={mediaUrl(m.url)} target="_blank" rel="noreferrer" data-testid={`media-open-${m.id}`}>Open</a>
              <button type="button" className="link danger" onClick={() => remove(m)} data-testid={`media-delete-${m.id}`}><Trash2 size={12} /></button>
            </div>
          </article>
        ))}
        {!items.length && <div className="empty-state" data-testid="media-empty">No files yet. Upload your first photo, video or document above.</div>}
      </div>
    </div>
  );
};
